import { motion, useScroll, useTransform } from "framer-motion";
import { Flame, FileSearch, Users } from "lucide-react";
import EmberCanvas from "@/components/EmberCanvas";
import { useLanguage } from "@/i18n/LanguageContext";
import { projectConfig, buyTarget } from "@/config/project";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useLanguage();
  const { scrollY } = useScroll();
  const bullY = useTransform(scrollY, [0, 700], [0, 90]);
  const glowOpacity = useTransform(scrollY, [0, 500], [1, 0.3]);

  const buyProps = buyTarget.external
    ? { href: buyTarget.href, target: "_blank", rel: "noopener noreferrer" }
    : { href: buyTarget.href };

  return (
    <section id="home" className="relative flex min-h-screen items-center overflow-hidden pt-16" data-testid="hero-section">
      {/* radial crimson glow */}
      <motion.div
        aria-hidden="true"
        style={{ opacity: glowOpacity }}
        className="absolute right-[-10%] top-1/2 h-[80vmin] w-[80vmin] -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(220,38,38,0.28),transparent_65%)]"
      />
      <EmberCanvas density={52} />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:items-center lg:px-8">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-red-800/50 bg-red-950/30 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.3em] text-red-400"
            data-testid="hero-kicker"
          >
            <Flame size={13} aria-hidden="true" />
            {t("hero.kicker")}
          </motion.p>

          <h1 data-testid="hero-heading">
            <MaskedLine
              delay={0.35}
              className="bg-gradient-to-r from-yellow-300 via-yellow-500 to-red-600 bg-clip-text font-zh text-6xl font-black leading-none text-transparent sm:text-8xl lg:text-9xl"
            >
              {projectConfig.chineseName}
            </MaskedLine>
            <MaskedLine
              delay={0.5}
              className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-zinc-50 sm:text-6xl lg:text-7xl"
            >
              {projectConfig.symbol}
            </MaskedLine>
            <MaskedLine
              delay={0.65}
              className="mt-4 font-display text-lg font-bold uppercase tracking-wide text-red-500 sm:text-2xl"
            >
              <span data-testid="hero-subheading">{t("hero.tagline")}</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-xl text-sm leading-relaxed text-zinc-400 sm:text-base"
            data-testid="hero-description"
          >
            {t("hero.description")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              {...buyProps}
              data-testid="hero-buy-sln-button"
              className="rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 px-8 py-3.5 text-sm font-bold tracking-[0.12em] text-black shadow-[0_0_36px_rgba(234,179,8,0.4)] transition-transform hover:-translate-y-1"
            >
              {t("hero.buy")}
            </a>
            <a
              href="#contract"
              data-testid="hero-view-contract-button"
              className="inline-flex items-center gap-2 rounded-full border border-red-700/60 px-8 py-3.5 text-sm font-bold tracking-[0.12em] text-red-400 transition-colors hover:border-red-500 hover:bg-red-950/40 hover:text-red-300"
            >
              <FileSearch size={16} aria-hidden="true" />
              {t("hero.viewContract")}
            </a>
            <a
              href="#community"
              data-testid="hero-join-community-button"
              className="inline-flex items-center gap-2 rounded-full border border-zinc-700/70 px-8 py-3.5 text-sm font-bold tracking-[0.12em] text-zinc-300 transition-colors hover:border-yellow-600/60 hover:text-yellow-400"
            >
              <Users size={16} aria-hidden="true" />
              {t("hero.joinCommunity")}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500"
          >
            <span className="rounded border border-zinc-800 px-3 py-1.5" data-testid="hero-badge-network">{t("hero.badgeNetwork")}</span>
            <span className="rounded border border-zinc-800 px-3 py-1.5" data-testid="hero-badge-chain">{t("hero.badgeChainId")}</span>
          </motion.div>
        </div>

        {/* Bull artwork with parallax */}
        <motion.div style={{ y: bullY }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.55, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto max-w-md"
          >
            <div aria-hidden="true" className="absolute -inset-8 rounded-full bg-[radial-gradient(circle,rgba(255,30,39,0.3),transparent_60%)] blur-2xl" />
            <div className="relative overflow-hidden rounded-2xl border border-yellow-700/40 shadow-[0_0_60px_rgba(220,38,38,0.35)]">
              <img
                src="/images/hero-bull.jpg"
                alt={t("hero.bullAlt")}
                data-testid="hero-bull-image"
                className="aspect-[4/5] w-full object-cover"
                loading="eager"
              />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[#050203] via-transparent to-transparent opacity-70" />
              <div className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-500/90">
                {projectConfig.chineseName} · {projectConfig.symbol} · BSC
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
